import { useDataGridColumnsState } from '@ibsys/kit';
import type {
  DataGridColumnOrderState,
  DataGridColumnVisibilityState,
  DataGridFilterState,
  DataGridSortingState,
} from '@ibsys/kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CustomChartTableColumn } from '@/entities/dashboards';
import { useDashboardPresets } from '@/entities/settings';
import { compareChartValues } from '../chartValues';
import type { IndexedTableRow } from './chartTable';

type Params = {
  /** Диаграмма, чью таблицу настраивают: её настройки личные и живут отдельно от состава дашборда */
  chartId: string;
  columns: CustomChartTableColumn[];
  rows: IndexedTableRow[];
};

/** Границы диапазона фильтра числовой колонки и колонки-даты. */
type RangeFilterValue = { from?: number | string; to?: number | string };

const isRangeFilter = (value: unknown): value is RangeFilterValue =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Строка проходит текстовый фильтр: сравнение по подстроке без учёта регистра. */
const matchesText = (value: string | number | null, filter: string): boolean =>
  value !== null && String(value).toLowerCase().includes(filter.toLowerCase());

/**
 * Строка проходит фильтр диапазона. Границы сравниваются тем же способом, что и упорядочение той
 * же колонки: даты — моментами времени, числа — величиной, — иначе отбор по дате шёл бы по
 * написанию, а по числу — посимвольно.
 */
const matchesRange = (
  value: string | number | null,
  range: RangeFilterValue,
  type: CustomChartTableColumn['type'],
): boolean => {
  if (value === null || value === '') return false;
  if (range.from !== undefined && compareChartValues(value, range.from as string | number, type) < 0) return false;
  if (range.to !== undefined && compareChartValues(value, range.to as string | number, type) > 0) return false;
  return true;
};

/**
 * Личный вид таблицы диаграммы: состав и порядок колонок, упорядочение и фильтры.
 *
 * Отбор и упорядочение идут по уже полученной выборке — новых обращений к бэкенду они не дают:
 * данные диаграммы целиком у клиента, и заново наполнять её, чтобы переставить те же строки,
 * незачем.
 *
 * Настройки хранятся по каждой диаграмме в настройках смотрящего, а не в составе дашборда,
 * поэтому правка дашборда их не сбрасывает, а кнопка сброса фильтров панели не затрагивает.
 */
export const useChartTableState = ({ chartId, columns, rows }: Params) => {
  const { chartTables, setChartTableSettings } = useDashboardPresets();
  // Сохранённый вид читается один раз, на заходе: дальше составом колонок владеет сам грид, и
  // перечитывание настроек после каждой записи возвращало бы таблицу к только что записанному.
  const savedRef = useRef(chartTables?.[chartId]);
  const saved = savedRef.current;

  const defaultOrder = useMemo<DataGridColumnOrderState>(() => columns.map((column) => column.code), [columns]);

  // Колонка, о которой сохранённые настройки ничего не знают, пользователем не скрывалась:
  // выборка могла обзавестись новой колонкой уже после того, как вид таблицы был записан.
  const initialVisibility = useMemo<DataGridColumnVisibilityState>(() => {
    const visibility: DataGridColumnVisibilityState = {};
    const savedVisible = saved?.visibleColumns ?? [];
    const known = new Set([...savedVisible, ...(saved?.columnOrder ?? [])]);
    columns.forEach((column) => {
      visibility[column.code] = known.has(column.code) ? savedVisible.includes(column.code) : true;
    });
    return visibility;
  }, [columns, saved]);

  const initialOrder = useMemo<DataGridColumnOrderState>(() => {
    const savedOrder = (saved?.columnOrder ?? []).filter((code) => defaultOrder.includes(code));
    const added = defaultOrder.filter((code) => !savedOrder.includes(code));
    return [...savedOrder, ...added];
  }, [defaultOrder, saved]);

  const { gridProps, panelProps } = useDataGridColumnsState({
    defaultOrder: initialOrder,
    defaultVisibility: initialVisibility,
  });

  const [sorting, setSorting] = useState<DataGridSortingState>(saved?.sort ?? []);
  const [filters, setFilters] = useState<DataGridFilterState>(saved?.filters ?? {});

  const persist = setChartTableSettings;
  const { columnOrder, columnVisibility } = gridProps;

  // Состав колонок приходит от панели грида отдельным событием, поэтому записывается только
  // изменившийся: иначе каждый рендер таблицы шёл бы в настройки пользователя.
  const lastColumnsRef = useRef<string | null>(null);
  useEffect(() => {
    const order = columnOrder ?? initialOrder;
    const visibility = columnVisibility ?? initialVisibility;
    const visibleColumns = order.filter((code) => visibility[code] !== false);
    const signature = JSON.stringify({ visibleColumns, order });
    if (lastColumnsRef.current === signature) return;
    lastColumnsRef.current = signature;
    persist(chartId, { visibleColumns, columnOrder: order });
  }, [chartId, columnOrder, columnVisibility, initialOrder, initialVisibility, persist]);

  const handleSortingChange = useCallback(
    (next: DataGridSortingState) => {
      setSorting(next);
      persist(chartId, { sort: next });
    },
    [chartId, persist],
  );

  const handleFiltersChange = useCallback(
    (next: DataGridFilterState) => {
      setFilters(next);
      persist(chartId, { filters: next });
    },
    [chartId, persist],
  );

  const columnByCode = useMemo(() => new Map(columns.map((column) => [column.code, column])), [columns]);

  const visibleRows = useMemo(() => {
    const conditions = Object.entries(filters).filter(([, value]) => value !== undefined && value !== null);
    const filtered =
      conditions.length === 0
        ? rows
        : rows.filter((row) =>
            conditions.every(([code, condition]) => {
              const column = columnByCode.get(code);
              // Фильтр по колонке, которой в выборке уже нет, строк не отсеивает: он остался от
              // прежнего наполнения и снимется, как только пользователь его тронет.
              if (!column) return true;
              const value = row.values[code] ?? null;
              if (typeof condition === 'string') return condition === '' || matchesText(value, condition);
              if (isRangeFilter(condition)) return matchesRange(value, condition, column.type);
              return true;
            }),
          );

    const [order] = sorting;
    const column = order && columnByCode.get(order.columnId);
    if (!order || !column) return filtered;
    const direction = order.direction === 'desc' ? -1 : 1;
    return [...filtered].sort(
      (first, second) =>
        direction *
        compareChartValues(first.values[column.code] ?? null, second.values[column.code] ?? null, column.type),
    );
  }, [rows, filters, sorting, columnByCode]);

  return {
    /** Пропы состава и порядка колонок для грида */
    gridProps,
    /** Сброс состава колонок к умолчанию — панель колонок грида */
    onResetColumns: panelProps.onReset,
    rows: visibleRows,
    sorting,
    onSortingChange: handleSortingChange,
    filters,
    onFiltersChange: handleFiltersChange,
  };
};
